import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EmployerHeader from '../components/employer/EmployerHeader';
import api from '../services/api';
import SimButton from '../components/ui/SimButton';
import SimBadge from '../sim/ui/SimBadge';
import SimCard from '../sim/ui/SimCard';
import SimShell from '../sim/ui/SimShell';
import { SimBody, SimLabel, SimTitle } from '../sim/ui/SimText';
import { simTokens } from '../sim/ui/tokens';

type EvidenceItem = {
  screen_code: string;
  row_key: string;
  task_type: string;
  prompt?: string;
  expected: number | null;
  actual: number | null;
  tolerance?: number;
  selected_option?: string | null;
  selected_option_label?: string | null;
  correct_option?: string | null;
  correct_option_label?: string | null;
  selected_options?: string[];
  selected_options_labels?: string[];
  correct_options?: string[];
  correct_options_labels?: string[];
  selected_pairs?: Array<{ leftId: string; rightId: string }>;
  correct_pairs?: Array<{ leftId: string; rightId: string }>;
  correct: boolean;
  points: number;
  points_available?: number;
  why_unscored?: string;
};

type ReportSnapshot = {
  candidate: {
    full_name: string;
    sa_id_last4: string;
  };
  simulation: {
    id: string;
    name: string;
    role_family: string;
    version: string;
  };
  variant: {
    variant_code: string;
    variant_name: string;
  };
  link?: {
    job_title?: string | null;
  };
  performance: {
    total_points_earned: number;
    total_points_available: number;
    percent_0_100: number;
    band: string;
  };
  per_screen_breakdown: Array<{
    screen_code: string;
    screen_name: string;
    points_earned: number;
    points_available: number;
    percent: number;
  }>;
  evidence?: EvidenceItem[];
  integrity: {
    level: 'HIGH' | 'MEDIUM' | 'LOW';
    flags: string[];
    summary: string[];
    metrics?: Record<string, unknown>;
  };
};

type EvidenceGroup = {
  screenCode: string;
  screenName: string;
  items: EvidenceItem[];
};

const HASH_TOKEN_PATTERN = /^[0-9a-f]{64}$/i;
const isDev = import.meta.env.DEV;
const DEFAULT_VISIBLE_PER_SCREEN = 5;
const EVIDENCE_STEP = 10;

const isEvidenceUnscored = (item: EvidenceItem): boolean => {
  if (typeof item.why_unscored === 'string' && item.why_unscored.trim().length > 0) return true;
  return typeof item.points_available === 'number' && item.points_available <= 0;
};

const isEvidenceIncorrect = (item: EvidenceItem): boolean => !isEvidenceUnscored(item) && !item.correct;

const getIntegrityBadgeVariant = (level: 'HIGH' | 'MEDIUM' | 'LOW') => {
  if (level === 'LOW') return 'integrity-low' as const;
  if (level === 'MEDIUM') return 'integrity-medium' as const;
  return 'integrity-high' as const;
};

const ReportPageFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <EmployerHeader />
    {children}
  </>
);

const buildDefaultVisibleMap = (screenCodes: string[]): Record<string, number> => {
  return screenCodes.reduce<Record<string, number>>((acc, screenCode) => {
    acc[screenCode] = DEFAULT_VISIBLE_PER_SCREEN;
    return acc;
  }, {});
};

const ReportLoadingState: React.FC = () => {
  return (
    <ReportPageFrame>
      <SimShell maxWidthClassName="max-w-5xl">
        <SimCard className="space-y-4">
          <div className="h-3 w-32 animate-pulse rounded-full bg-slate-700/70" />
          <div className="h-8 w-80 animate-pulse rounded-input bg-slate-600/70" />
          <div className="h-4 w-56 animate-pulse rounded-full bg-slate-700/70" />
        </SimCard>
        <SimCard className="space-y-3">
          <div className="h-4 w-full animate-pulse rounded-input bg-slate-700/60" />
          <div className="h-4 w-11/12 animate-pulse rounded-input bg-slate-700/60" />
          <div className="h-4 w-10/12 animate-pulse rounded-input bg-slate-700/60" />
        </SimCard>
      </SimShell>
    </ReportPageFrame>
  );
};

const SimReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { reportToken } = useParams();

  const [report, setReport] = useState<ReportSnapshot | null>(null);
  const [evidenceItems, setEvidenceItems] = useState<EvidenceItem[] | null>(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [invalidLink, setInvalidLink] = useState(false);
  const [hashLinkUsed, setHashLinkUsed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [incorrectOnly, setIncorrectOnly] = useState(false);
  const [visibleCountByScreenCode, setVisibleCountByScreenCode] = useState<Record<string, number>>({});
  const [evidenceRetryNonce, setEvidenceRetryNonce] = useState(0);

  const reportRequestIdRef = useRef(0);
  const evidenceRequestIdRef = useRef(0);
  const reviewedRequestTokenRef = useRef<string | null>(null);

  const resetVisibleCounts = useCallback((nextReport: ReportSnapshot | null) => {
    const screenCodes = nextReport?.per_screen_breakdown.map((row) => row.screen_code) || [];
    setVisibleCountByScreenCode(buildDefaultVisibleMap(screenCodes));
  }, []);

  useEffect(() => {
    const requestId = reportRequestIdRef.current + 1;
    reportRequestIdRef.current = requestId;
    const controller = new AbortController();
    const timingLabel = `sim-report-fetch-summary:${reportToken || 'missing'}:${requestId}`;
    if (isDev) console.time(timingLabel);

    const loadReportSummary = async () => {
      if (!reportToken) {
        setInvalidLink(true);
        setLoading(false);
        if (isDev) console.timeEnd(timingLabel);
        return;
      }

      if (HASH_TOKEN_PATTERN.test(reportToken)) {
        setHashLinkUsed(true);
        setLoading(false);
        if (isDev) console.timeEnd(timingLabel);
        return;
      }

      try {
        const response = await api.get<ReportSnapshot>(`/sim/reports/${reportToken}?includeEvidence=0`, {
          signal: controller.signal,
        });
        if (reportRequestIdRef.current !== requestId) return;
        setReport(response.data);
        setEvidenceItems(null);
        setEvidenceError(null);
        resetVisibleCounts(response.data);
      } catch (err: any) {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        if (reportRequestIdRef.current !== requestId) return;
        if (err?.response?.status === 401) {
          setInvalidLink(true);
        } else {
          setError(err?.response?.data?.error?.message || 'Failed to load report.');
        }
      } finally {
        if (reportRequestIdRef.current === requestId) {
          setLoading(false);
        }
        if (isDev) console.timeEnd(timingLabel);
      }
    };

    setLoading(true);
    setInvalidLink(false);
    setHashLinkUsed(false);
    setError(null);
    setReport(null);
    setEvidenceItems(null);
    setEvidenceError(null);
    setIncorrectOnly(false);
    setVisibleCountByScreenCode({});
    loadReportSummary();

    return () => {
      controller.abort();
    };
  }, [reportToken, resetVisibleCounts]);

  useEffect(() => {
    if (!report || evidenceItems !== null || !reportToken) return;

    const requestId = evidenceRequestIdRef.current + 1;
    evidenceRequestIdRef.current = requestId;
    const controller = new AbortController();
    const timingLabel = `sim-report-fetch-evidence:${reportToken}:${requestId}`;
    if (isDev) console.time(timingLabel);

    let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
    let fallbackTimeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
    let idleId: number | null = null;
    let hasStarted = false;

    const loadEvidence = async () => {
      if (hasStarted) return;
      hasStarted = true;
      setEvidenceLoading(true);
      setEvidenceError(null);
      try {
        const response = await api.get<ReportSnapshot>(`/sim/reports/${reportToken}?includeEvidence=1`, {
          signal: controller.signal,
        });
        if (evidenceRequestIdRef.current !== requestId) return;
        setEvidenceItems(Array.isArray(response.data.evidence) ? response.data.evidence : []);
      } catch (err: any) {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        setEvidenceItems([]);
        setEvidenceError('Unable to load evidence right now.');
      } finally {
        if (evidenceRequestIdRef.current === requestId) {
          setEvidenceLoading(false);
        }
        if (isDev) console.timeEnd(timingLabel);
      }
    };

    const idleCapableGlobal = globalThis as typeof globalThis & {
      requestIdleCallback?: (cb: IdleRequestCallback) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    if (typeof idleCapableGlobal.requestIdleCallback === 'function') {
      idleId = idleCapableGlobal.requestIdleCallback(() => {
        loadEvidence();
      });
      fallbackTimeoutId = globalThis.setTimeout(() => {
        loadEvidence();
      }, 500);
    } else {
      timeoutId = globalThis.setTimeout(() => {
        loadEvidence();
      }, 500);
    }

    return () => {
      if (idleId !== null && typeof idleCapableGlobal.cancelIdleCallback === 'function') {
        idleCapableGlobal.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        globalThis.clearTimeout(timeoutId);
      }
      if (fallbackTimeoutId !== null) {
        globalThis.clearTimeout(fallbackTimeoutId);
      }
      controller.abort();
    };
  }, [evidenceItems, evidenceRetryNonce, report, reportToken]);

  useEffect(() => {
    if (!report || !reportToken || invalidLink || hashLinkUsed) return;
    if (reviewedRequestTokenRef.current === reportToken) return;
    reviewedRequestTokenRef.current = reportToken;

    void Promise.resolve(api.post(`/sim/employer/reports/${encodeURIComponent(reportToken)}/reviewed`)).catch(() => {
      // Viewing still works even if reviewed-state persistence is unavailable.
    });
  }, [hashLinkUsed, invalidLink, report, reportToken]);

  const evidenceGroups = useMemo(() => {
    if (!report || !Array.isArray(evidenceItems)) return [] as EvidenceGroup[];

    const byScreen: Record<string, EvidenceItem[]> = {};
    evidenceItems.forEach((item) => {
      if (!byScreen[item.screen_code]) byScreen[item.screen_code] = [];
      byScreen[item.screen_code].push(item);
    });

    return report.per_screen_breakdown.map((screenRow) => {
      const source = byScreen[screenRow.screen_code] || [];
      const filtered = incorrectOnly ? source.filter((item) => isEvidenceIncorrect(item)) : source;
      const sorted = [...filtered].sort((a, b) => {
        const correctnessDelta = Number(isEvidenceIncorrect(a)) - Number(isEvidenceIncorrect(b));
        if (correctnessDelta !== 0) return correctnessDelta;
        return a.row_key.localeCompare(b.row_key);
      });

      return {
        screenCode: screenRow.screen_code,
        screenName: screenRow.screen_name,
        items: sorted,
      };
    });
  }, [evidenceItems, incorrectOnly, report]);

  const handleToggleIncorrectOnly = useCallback(() => {
    setIncorrectOnly((prev) => !prev);
    resetVisibleCounts(report);
  }, [report, resetVisibleCounts]);

  const handleShowMore = useCallback((screenCode: string, total: number) => {
    setVisibleCountByScreenCode((prev) => {
      const current = prev[screenCode] || DEFAULT_VISIBLE_PER_SCREEN;
      return {
        ...prev,
        [screenCode]: Math.min(total, current + EVIDENCE_STEP),
      };
    });
  }, []);

  const handleShowLess = useCallback((screenCode: string) => {
    setVisibleCountByScreenCode((prev) => ({
      ...prev,
      [screenCode]: DEFAULT_VISIBLE_PER_SCREEN,
    }));
  }, []);

  const handleRetryEvidence = useCallback(() => {
    setEvidenceError(null);
    setEvidenceLoading(false);
    setEvidenceItems(null);
    setEvidenceRetryNonce((prev) => prev + 1);
  }, []);

  if (loading) {
    return <ReportLoadingState />;
  }

  if (hashLinkUsed) {
    return (
      <ReportPageFrame>
        <SimShell
          maxWidthClassName="max-w-3xl"
          className="flex items-center"
          fixedAction={
            <SimButton onClick={() => navigate('/employer/dashboard')}>Back</SimButton>
          }
        >
          <SimCard className="space-y-4 text-center">
            <SimLabel className="text-rose-300">Report Link</SimLabel>
            <SimTitle className="text-xl sm:text-2xl">This is not a report link</SimTitle>
            <SimBody>
              You pasted an internal `report_token_hash` value. Use the report URL token from the employer results page instead.
            </SimBody>
          </SimCard>
        </SimShell>
      </ReportPageFrame>
    );
  }

  if (invalidLink) {
    return (
      <ReportPageFrame>
        <SimShell
          maxWidthClassName="max-w-3xl"
          className="flex items-center"
          fixedAction={<SimButton onClick={() => navigate('/employer/dashboard')}>Back</SimButton>}
        >
          <SimCard className="space-y-4 text-center">
            <SimLabel className="text-rose-300">Report</SimLabel>
            <SimTitle className="text-xl sm:text-2xl">Invalid or expired report link</SimTitle>
            <SimBody>This report link is not valid. Please request a fresh employer report URL.</SimBody>
          </SimCard>
        </SimShell>
      </ReportPageFrame>
    );
  }

  if (error || !report) {
    return (
      <ReportPageFrame>
        <SimShell
          maxWidthClassName="max-w-3xl"
          className="flex items-center"
          fixedAction={
            <SimButton onClick={() => navigate('/employer/sim/results')}>
              Back
            </SimButton>
          }
        >
          <SimCard className="space-y-4 text-center">
            <SimLabel className="text-rose-300">Error</SimLabel>
            <SimTitle className="text-xl sm:text-2xl">Couldn’t load this report</SimTitle>
            <SimBody>{error || 'Please retry.'}</SimBody>
            <div className="flex justify-center gap-3">
              <SimButton onClick={() => window.location.reload()}>Retry</SimButton>
            </div>
          </SimCard>
        </SimShell>
      </ReportPageFrame>
    );
  }

  const noEvidenceCaptured = Array.isArray(evidenceItems) && evidenceItems.length === 0;

  return (
    <ReportPageFrame>
      <SimShell
        maxWidthClassName="max-w-5xl"
        fixedAction={
          <SimButton onClick={() => navigate('/employer/sim/results')}>
            Back
          </SimButton>
        }
        header={
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className={simTokens.headerAccentLabel}>Simulation Report</p>
                <p className="text-lg font-semibold text-txt-primary">
                  {report.simulation.name} • {report.variant.variant_code}
                </p>
                <p className="mt-1 text-sm text-txt-muted">
                  {report.variant.variant_name} • {report.simulation.role_family} • v{report.simulation.version}
                  {report.link?.job_title ? ` • ${report.link.job_title}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <SimButton variant="secondary" onClick={() => window.print()}>
                  Print
                </SimButton>
              </div>
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-txt-secondary/85">
              Candidate: {report.candidate.full_name} • ID: {report.candidate.sa_id_last4}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <SimBadge variant="neutral">
                {report.performance.percent_0_100}% • {report.performance.band}
              </SimBadge>
              <SimBadge variant={getIntegrityBadgeVariant(report.integrity.level)}>
                Integrity {report.integrity.level}
              </SimBadge>
            </div>
          </div>
        }
      >
      <div className="space-y-6">
        <SimCard className="space-y-4">
          <SimLabel>Executive Summary</SimLabel>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-card border border-line bg-surface-page/40 p-4">
              <p className="text-sm text-txt-muted">Performance</p>
              <p className="mt-2 text-3xl font-semibold text-txt-primary">{report.performance.percent_0_100}%</p>
              <p className="mt-1 text-sm text-txt-secondary">{report.performance.band}</p>
              <p className="mt-1 text-xs text-txt-muted">
                {report.performance.total_points_earned}/{report.performance.total_points_available} points
              </p>
            </div>
            <div className="rounded-card border border-line bg-surface-page/40 p-4">
              <p className="text-sm text-txt-muted">Integrity Confidence</p>
              <p className="mt-2 text-2xl font-semibold text-txt-primary">{report.integrity.level}</p>
              <div className="mt-2 space-y-1">
                {(report.integrity.summary || []).slice(0, 3).map((line, index) => (
                  <p key={`${line}-${index}`} className="text-sm text-txt-secondary">
                    • {line}
                  </p>
                ))}
              </div>
            </div>
          </div>
          {report.integrity.level === 'LOW' ? (
            <div className="rounded-input border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
              Recommended: retest or follow-up verification.
            </div>
          ) : null}
        </SimCard>

        <SimCard className="space-y-4">
          <SimLabel>Screen Breakdown</SimLabel>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[38rem] text-left text-sm text-txt-secondary">
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-[0.18em] text-txt-muted">
                  <th className="px-3 py-2">Screen</th>
                  <th className="px-3 py-2">Code</th>
                  <th className="px-3 py-2">Score</th>
                  <th className="px-3 py-2">Percent</th>
                </tr>
              </thead>
              <tbody>
                {report.per_screen_breakdown.map((row) => (
                  <tr key={row.screen_code} className="border-b border-white/10 last:border-b-0">
                    <td className="px-3 py-3 text-txt-primary">{row.screen_name}</td>
                    <td className="px-3 py-3 font-mono text-xs text-txt-muted">{row.screen_code}</td>
                    <td className="px-3 py-3">
                      {row.points_earned}/{row.points_available}
                    </td>
                    <td className="px-3 py-3">{row.percent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SimCard>

        <SimCard className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SimLabel>Evidence</SimLabel>
            <SimButton variant="secondary" onClick={handleToggleIncorrectOnly}>
              {incorrectOnly ? 'Show all' : 'Incorrect only'}
            </SimButton>
          </div>

          {evidenceLoading && !Array.isArray(evidenceItems) ? (
            <p className="text-sm text-txt-muted">Loading evidence...</p>
          ) : evidenceError ? (
            <div className="space-y-3">
              <p className="text-sm text-txt-muted">{evidenceError}</p>
              <div className="flex">
                <SimButton variant="secondary" onClick={handleRetryEvidence}>
                  Retry evidence load
                </SimButton>
              </div>
            </div>
          ) : noEvidenceCaptured ? (
            <p className="text-sm text-txt-muted">No evidence captured for this attempt.</p>
          ) : (
            <div className="space-y-4">
              {evidenceGroups.map((group) => {
                const totalItems = group.items.length;
                const visibleCount = Math.min(
                  totalItems,
                  visibleCountByScreenCode[group.screenCode] || DEFAULT_VISIBLE_PER_SCREEN
                );
                const visibleItems = group.items.slice(0, visibleCount);

                return (
                  <div key={group.screenCode} className="rounded-card border border-line bg-surface-page/40 p-4">
                    <p className="text-sm font-medium text-txt-primary">
                      {group.screenName}
                      <span className="ml-2 font-mono text-xs text-txt-secondary/75">{group.screenCode}</span>
                    </p>

                    {incorrectOnly && totalItems === 0 ? (
                      <p className="mt-3 text-sm text-txt-muted">No incorrect items on this screen.</p>
                    ) : (
                      <>
                        <p className="mt-2 text-xs uppercase tracking-[0.16em] text-txt-secondary/75">
                          Showing {visibleCount} of {totalItems}
                        </p>
                        <div className="mt-3 space-y-3">
                          {visibleItems.map((item) => (
                            <div
                              key={`${item.screen_code}:${item.row_key}`}
                              className="rounded-input border border-line bg-surface-card/50 p-3"
                            >
                              {(() => {
                                const unscored = isEvidenceUnscored(item);
                                const isIncorrect = isEvidenceIncorrect(item);
                                return (
                                  <>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <p className="font-mono text-xs text-txt-muted">
                                        {item.screen_code}:{item.row_key}
                                      </p>
                                      <SimBadge variant={unscored ? 'neutral' : isIncorrect ? 'evidence-incorrect' : 'evidence-correct'}>
                                        {unscored ? 'Unscored' : isIncorrect ? 'Incorrect' : 'Correct'}
                                      </SimBadge>
                                    </div>
                                    {item.prompt ? <p className="mt-2 text-sm text-txt-secondary">{item.prompt}</p> : null}
                                    <div className="mt-2 grid gap-2 text-sm text-txt-secondary sm:grid-cols-2">
                                      {item.task_type === 'NumericEntry' ? (
                                        <>
                                          <p>Expected: {item.expected === null ? '—' : String(item.expected)}</p>
                                          <p>Actual: {item.actual === null ? '—' : String(item.actual)}</p>
                                          {typeof item.tolerance === 'number' ? (
                                            <p>Tolerance: {item.tolerance}</p>
                                          ) : (
                                            <p>Tolerance: —</p>
                                          )}
                                        </>
                                      ) : null}
                                      {item.task_type === 'SingleChoice' || item.task_type === 'Picklist' ? (
                                        <>
                                          <p>
                                            Selected option:{' '}
                                            {typeof item.selected_option_label === 'string'
                                              ? item.selected_option_label
                                              : typeof item.selected_option === 'string'
                                                ? item.selected_option
                                                : '—'}
                                          </p>
                                          <p>
                                            Correct option:{' '}
                                            {typeof item.correct_option_label === 'string'
                                              ? item.correct_option_label
                                              : typeof item.correct_option === 'string'
                                                ? item.correct_option
                                                : '—'}
                                          </p>
                                        </>
                                      ) : null}
                                      {item.task_type === 'MultiSelect' ? (
                                        <>
                                          <p>
                                            Selected options:{' '}
                                            {Array.isArray(item.selected_options_labels)
                                              ? item.selected_options_labels.join(', ') || '—'
                                              : Array.isArray(item.selected_options)
                                                ? item.selected_options.join(', ') || '—'
                                                : '—'}
                                          </p>
                                          <p>
                                            Correct options:{' '}
                                            {Array.isArray(item.correct_options_labels)
                                              ? item.correct_options_labels.join(', ') || '—'
                                              : Array.isArray(item.correct_options)
                                                ? item.correct_options.join(', ') || '—'
                                                : '—'}
                                          </p>
                                        </>
                                      ) : null}
                                      {item.task_type === 'MatchingPairs' ? (
                                        <>
                                          <p>
                                            Selected pairs:{' '}
                                            {Array.isArray(item.selected_pairs)
                                              ? item.selected_pairs.map((pair) => `${pair.leftId}->${pair.rightId}`).join(', ') || '—'
                                              : '—'}
                                          </p>
                                          <p>
                                            Correct pairs:{' '}
                                            {Array.isArray(item.correct_pairs)
                                              ? item.correct_pairs.map((pair) => `${pair.leftId}->${pair.rightId}`).join(', ') || '—'
                                              : '—'}
                                          </p>
                                        </>
                                      ) : null}
                                      {unscored && item.why_unscored ? <p>Reason: {item.why_unscored}</p> : null}
                                      <p>
                                        Points:{' '}
                                        {unscored
                                          ? 'Unscored'
                                          : `${item.points}${typeof item.points_available === 'number' ? ` / ${item.points_available}` : ''}`}
                                      </p>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          ))}
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                          {totalItems > visibleCount ? (
                            <SimButton
                              variant="secondary"
                              onClick={() => handleShowMore(group.screenCode, totalItems)}
                            >
                              Show more
                            </SimButton>
                          ) : null}
                          {visibleCount > DEFAULT_VISIBLE_PER_SCREEN ? (
                            <SimButton variant="secondary" onClick={() => handleShowLess(group.screenCode)}>
                              Show less
                            </SimButton>
                          ) : null}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </SimCard>

        <SimCard className="space-y-4">
          <SimLabel>Integrity Detail</SimLabel>
          <div className="flex flex-wrap items-center gap-3">
            <SimBadge variant={getIntegrityBadgeVariant(report.integrity.level)}>
              {report.integrity.level}
            </SimBadge>
            {(report.integrity.flags || []).map((flag) => (
              <SimBadge key={flag} variant="flag">
                {flag}
              </SimBadge>
            ))}
          </div>

          {(report.integrity.summary || []).length > 0 ? (
            <div className="space-y-1">
              {(report.integrity.summary || []).map((line, index) => (
                <p key={`${line}-${index}`} className="text-sm text-txt-secondary">
                  • {line}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-sm text-txt-muted">No additional integrity notes recorded.</p>
          )}

          {report.integrity.metrics && Object.keys(report.integrity.metrics).length > 0 ? (
            <div className="rounded-card border border-line bg-surface-page/40 p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-txt-muted">Metrics</p>
              <div className="mt-2 space-y-1 text-sm text-txt-secondary">
                {Object.entries(report.integrity.metrics).map(([key, value]) => (
                  <p key={key}>
                    {key}: {typeof value === 'number' ? value : String(value)}
                  </p>
                ))}
              </div>
            </div>
          ) : null}
        </SimCard>
      </div>
      </SimShell>
    </ReportPageFrame>
  );
};

export default SimReportPage;
